import React, { useRef, useEffect, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { floorSize, theColor } from './App';
import { Trail } from '@react-three/drei'

// Shared camera state for coordinating between components
const cameraState = {
  orbitAngle: 0,
  verticalAngle: Math.PI / 2 // Default vertical angle (45 degrees)
};

// Third person camera that follows the player
function ThirdPersonCamera({ playerPosition }) {
    const { camera } = useThree();
    const orbitAngle = useRef(cameraState.orbitAngle);
    const verticalAngle = useRef(cameraState.verticalAngle);
    const isDragging = useRef(false);
    const prevMousePosition = useRef({ x: 0, y: 0 });
    const prevTouchPosition = useRef({ x: 0, y: 0 });
    const isTouching = useRef(false);
    const bottomPixelsNotAllowed = 205;
    
    // Add animation state tracking
    const isAnimating = useRef(true);
    const animationStep = useRef(0);
    const animationProgress = useRef(0);
    const animationStart = useRef(null);
    const hasAnimated = useRef(false); // New ref to track if animation has run
    
    const step1Duration = 2000; // 2 seconds to go from PI/2 to PI/32
    const step2Duration = 1500; // 1.5 seconds to go from PI/32 to PI/6

    // Run initialization animation once
    useEffect(() => {
      if (!hasAnimated.current) { // Check if animation has already run
        // Initial vertical angle is already Math.PI/2 from cameraState
        // Set up animation start time
        animationStart.current = Date.now();
        isAnimating.current = true;
        hasAnimated.current = true; // Set to true after first run
        
        // Define animation steps durations (in milliseconds)

        
        // Animation step 1: PI/2 to PI/32
        const step1Timer = setTimeout(() => {
          animationStep.current = 1;
          animationStart.current = Date.now();
          animationProgress.current = 0;
        }, step1Duration);
        
        // Animation step 2: PI/32 to PI/6
        const step2Timer = setTimeout(() => {
          animationStep.current = 2;
          animationStart.current = Date.now();
          animationProgress.current = 0;
        }, step1Duration + step2Duration);
        
        // Complete animation
        const completeTimer = setTimeout(() => {
          isAnimating.current = false;
          // Set final position
          verticalAngle.current = Math.PI/6;
          cameraState.verticalAngle = Math.PI/6;
        }, step1Duration + step2Duration + 500); // Add a small buffer
        
        return () => {
          // Clean up timers
          clearTimeout(step1Timer);
          clearTimeout(step2Timer);
          clearTimeout(completeTimer);
        };
      }
    }, []);
    
    // Set up mouse and touch event listeners
    useEffect(() => {
      // Consolidated function to update camera angle based on pointer movement
      const updateCameraAngle = (deltaY, isTouchInput = false) => {
        // Different sensitivity for touch vs mouse
        const sensitivity = isTouchInput ? 0.005 : 0.0025;
        
        // Apply vertical angle change
        verticalAngle.current += deltaY * sensitivity;
        
        // Limit vertical angle to prevent camera flipping (same for both inputs)
        verticalAngle.current = Math.max(0.1, Math.min(Math.PI / 2, verticalAngle.current));
        
        // Update shared camera state
        cameraState.verticalAngle = verticalAngle.current;
      };
      
      // === Mouse Controls ===
      const handleMouseDown = (e) => {
        isDragging.current = true;
        prevMousePosition.current = { x: e.clientX, y: e.clientY };
      };
      
      const handleMouseUp = () => {
        isDragging.current = false;
      };
      
      const handleMouseMove = (e) => {
        if (isDragging.current) {
          // Calculate mouse movement
          const deltaY = e.clientY - prevMousePosition.current.y;
          
          // Update previous mouse position
          prevMousePosition.current = { x: e.clientX, y: e.clientY };
          
          // Update camera angle with mouse input
          updateCameraAngle(deltaY, false);
        }
      };
      
      // === Touch Controls ===
      const handleTouchStart = (e) => {
        if (e.touches.length === 1) {
          const touchY = e.touches[0].clientY;
          const screenHeight = window.innerHeight;
          const isAboveNoTouchZone = touchY < (screenHeight - bottomPixelsNotAllowed);
          
          // Only enable camera control if touch starts above the restricted zone
          if (isAboveNoTouchZone) {
            isTouching.current = true;
            prevTouchPosition.current = { 
              x: e.touches[0].clientX, 
              y: e.touches[0].clientY 
            };
          } else {
            // Touch is in the restricted zone, don't allow camera movement
            isTouching.current = false;
          }
        }
      };
      
      const handleTouchEnd = () => {
        isTouching.current = false;
      };
      
      const handleTouchMove = (e) => {
        // Only handle single-finger vertical swipes for camera control
        if (isTouching.current && e.touches.length === 1) {
          const deltaY = e.touches[0].clientY - prevTouchPosition.current.y;
          
          // Update previous touch position
          prevTouchPosition.current = { 
            x: e.touches[0].clientX, 
            y: e.touches[0].clientY 
          };
          
          // Update camera angle with touch input
          updateCameraAngle(deltaY, true);
          
          // Prevent default behavior (page scrolling)
          e.preventDefault();
        }
      };
      
      // Define mouse events
      const mouseEvents = {
        'mousedown': handleMouseDown,
        'mouseup': handleMouseUp,
        'mousemove': handleMouseMove,
        'mouseleave': handleMouseUp
      };
      
      // Add mouse event listeners
      Object.entries(mouseEvents).forEach(([event, handler]) => {
        window.addEventListener(event, handler);
      });
      
      // Add touch event listeners with proper options
      window.addEventListener('touchstart', handleTouchStart, { passive: false });
      window.addEventListener('touchend', handleTouchEnd);
      window.addEventListener('touchcancel', handleTouchEnd);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      
      return () => {
        // Remove mouse event listeners on cleanup
        Object.entries(mouseEvents).forEach(([event, handler]) => {
          window.removeEventListener(event, handler);
        });
        
        // Remove touch event listeners
        window.removeEventListener('touchstart', handleTouchStart);
        window.removeEventListener('touchend', handleTouchEnd);
        window.removeEventListener('touchcancel', handleTouchEnd);
        window.removeEventListener('touchmove', handleTouchMove);
      };
    }, []);
   
    useFrame(() => {
      if (playerPosition) {
        // Handle initialization animation
        if (isAnimating.current) {
          const now = Date.now();
          const elapsed = now - animationStart.current;
          
          if (animationStep.current === 0) {
            // Step 1: Lerp from PI/2 to PI/32
            const duration = 2000; // 2 seconds
            const t = Math.min(elapsed / duration, 1);
            animationProgress.current = t;
            
            // Easing function for smoother motion (ease-out)
            const easedT = 1 - Math.pow(1 - t, 3);
            
            // Lerp from PI/2 to PI/32
            verticalAngle.current = THREE.MathUtils.lerp(
              Math.PI/2,
              Math.PI/32,
              easedT
            );
          } else if (animationStep.current === 1) {
            // Step 2: Lerp from PI/32 to PI/6
            const duration = 1500; // 1.5 seconds
            const t = Math.min(elapsed / duration, 1);
            animationProgress.current = t;
            
            // Easing function (ease-in-out)
            const easedT = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
            
            // Lerp from PI/32 to PI/6
            verticalAngle.current = THREE.MathUtils.lerp(
              Math.PI/32,
              Math.PI/6,
              easedT
            );
          }
          
          // Update shared camera state
          cameraState.verticalAngle = verticalAngle.current;
        }
      
        const [x, y, z] = playerPosition;
        
        // Camera configuration constants
        const cameraConfig = {
          minAngle: 0.1,
          maxAngle: Math.PI / 2,
          minDistance: 10,
          maxDistance: 80
        };
        
        // Calculate camera distance using interpolation
        const angleRange = cameraConfig.maxAngle - cameraConfig.minAngle;
        const distanceRange = cameraConfig.maxDistance - cameraConfig.minDistance;
        const angleProgress = (verticalAngle.current - cameraConfig.minAngle) / angleRange;
        
        const cameraDistance = cameraConfig.minDistance + (angleProgress * distanceRange);
        
        // Calculate orbital position based on vertical angle
        const horizontalDistance = cameraDistance * Math.cos(verticalAngle.current);
        const verticalDistance = cameraDistance * Math.sin(verticalAngle.current);
        
        // Calculate camera position based on both orbit and vertical angles
        const cameraX = x + horizontalDistance * Math.sin(orbitAngle.current);
        const cameraY = y + verticalDistance;
        const cameraZ = z + horizontalDistance * Math.cos(orbitAngle.current);
        
        camera.position.set(cameraX, cameraY, cameraZ);
        
        // Look at the player - 2
        camera.lookAt(x, y, z);
        
        // Keep orbit angle in sync with the shared state
        orbitAngle.current = cameraState.orbitAngle;
      }
    });
    
    return null;
}

// Function to create a circle mesh with an outline
export const createCircleMesh = (position) => {
  return (
    <>
      <mesh position={[position[0], position[1], position[2] - 0.003]} scale={[1.4,1.4,1.4]}>
        <circleGeometry args={[0.1, 8]} />
        <meshBasicMaterial color="black" side={THREE.DoubleSide} />
      </mesh>
      <mesh position={position}>
        <circleGeometry args={[0.1, 8]} />
        <meshBasicMaterial color="white" side={THREE.DoubleSide} />
      </mesh>
    </>
  );
};


// Character with a distinct face that rotates to face movement direction
export function Character({ 
  joystickInput = { x: 0, y: 0, force: 0 }, 
  setPlayerPosition,
  floorSize = 20,
  restrictedRadius,
  flagToTriggerHandleHit,
  isFemale,
  toggleAngleFlag
}) {
  const [position, setPosition] = useState([0, 1, 24]);
  const groupRef = useRef();
  const keysPressed = useRef({});
  const joystickRef = useRef(joystickInput);
  const [rotation, setRotation] = useState(Math.PI);
  const lastMovementDirection = useRef(null);
  const targetRotationRef = useRef(0);

  const angleToDance = isFemale ? 12 : 16

  const [currentAngle, setCurrentAngle] = useState(0); // Start at one of the angles
  
  const targetAngle = useRef(Math.PI / -angleToDance); // Set the target angle
  const resetTimeoutRef = useRef(null); // Ref to store the timeout
  
  // Toggle angle function
  const toggleAngle = () => {
    targetAngle.current = targetAngle.current === Math.PI / -angleToDance ? Math.PI / angleToDance : Math.PI / -angleToDance;
    
    // Clear the previous timeout if toggled
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
    }

    // Set a new timeout to reset the angle back to zero after 1 second
    resetTimeoutRef.current = setTimeout(() => {
      targetAngle.current = 0; // Reset target angle to zero
    }, 1000); // 1 second
  };
  
  // Add this near your other useRef declarations at the top of your component
  const isFirstRender = useRef(true);
  
  // Then modify the useEffect that contains these timeouts
  useEffect(() => {
    // Only run this animation sequence on the first render
    if (isFirstRender.current) {
      // Set the flag to false so this won't run again
      isFirstRender.current = false;
      
      setTimeout(() => {
        toggleAngle();
      }, 500);
      
      setTimeout(() => {
        toggleAngle();
      }, 1000);
      
      setTimeout(() => {
        toggleAngle();
      }, 1500);
    }
  }, []);
  
  // Update joystick ref when the prop changes
  useEffect(() => {
    joystickRef.current = joystickInput;
  }, [joystickInput]);
  
  // Set up key listeners for WASD controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      keysPressed.current[e.key.toLowerCase()] = true;
    };
    
    const handleKeyUp = (e) => {
      keysPressed.current[e.key.toLowerCase()] = false;
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);
  
  // Helper function to find the shortest angle for rotation
  const lerpAngle = (a, b, t) => {
    // Normalize angles to be between 0 and 2π
    while (a < 0) a += Math.PI * 2;
    while (b < 0) b += Math.PI * 2;
    while (a >= Math.PI * 2) a -= Math.PI * 2;
    while (b >= Math.PI * 2) b -= Math.PI * 2;
    
    // Find the shortest direction (clockwise or counterclockwise)
    let delta = b - a;
    
    // If we need to rotate more than 180 degrees, go the other way
    if (Math.abs(delta) > Math.PI) {
      if (delta > 0) {
        delta = delta - Math.PI * 2;
      } else {
        delta = delta + Math.PI * 2;
      }
    }
    
    // Return interpolated angle
    return a + delta * t;
  };
  
  // Consolidated function to check all movement boundaries
  const isValidPosition = (position) => {
    const halfFloorSize = floorSize / 2;
    
    // Since the floor is rotated by Math.PI/4 (45 degrees), transform coordinates
    const cos = Math.cos(Math.PI / 4);
    const sin = Math.sin(Math.PI / 4);
    
    // Transform world coordinates to the floor's rotated coordinate system
    const rotatedX = position[0] * cos + position[2] * sin;
    const rotatedZ = -position[0] * sin + position[2] * cos;
    
    // Calculate distance from center for the restricted circle check
    const distanceFromCenter = Math.sqrt(rotatedX * rotatedX + rotatedZ * rotatedZ);
    
    // Valid position is within outer bounds AND not within the restricted inner circle
    return (
      Math.abs(rotatedX) < halfFloorSize &&  // Within outer diamond bounds
      Math.abs(rotatedZ) < halfFloorSize &&  // Within outer diamond bounds
      distanceFromCenter >= restrictedRadius  // Not within restricted circle
    );
  };
  
  // Handle camera-relative movement and character rotation
  useFrame(() => {
    if (groupRef.current) {
      const moveSpeed = 0.22;
      let moved = false;
      let newPosition = [...position];
      let movementAngle = null;
      
      // Get camera angle to determine movement directions
      const angle = cameraState.orbitAngle;
      
      // Handle keyboard input (WASD)
      if (keysPressed.current['w']) {
        newPosition[0] -= Math.sin(angle) * moveSpeed;
        newPosition[2] -= Math.cos(angle) * moveSpeed;
        moved = true;
        movementAngle = angle;
      }
      if (keysPressed.current['s']) {
        newPosition[0] += Math.sin(angle) * moveSpeed;
        newPosition[2] += Math.cos(angle) * moveSpeed;
        moved = true;
        movementAngle = angle + Math.PI;
      }
      if (keysPressed.current['a']) {
        newPosition[0] -= Math.cos(angle) * moveSpeed;
        newPosition[2] -= Math.sin(angle) * moveSpeed;
        moved = true;
        movementAngle = angle + Math.PI / 2;
      }
      if (keysPressed.current['d']) {
        newPosition[0] += Math.cos(angle) * moveSpeed;
        newPosition[2] += Math.sin(angle) * moveSpeed;
        moved = true;
        movementAngle = angle - Math.PI / 2;
      }
      
      // Handle joystick input
      const { x, y, force } = joystickRef.current;
      if (force > 0.1) {
        const joystickAngle = Math.atan2(x, y);
        const moveAngle = angle + joystickAngle;
        const touchAux = 1.9;
        newPosition[0] += Math.sin(moveAngle) * moveSpeed * touchAux;
        newPosition[2] += Math.cos(moveAngle) * moveSpeed * touchAux;
        moved = true;
        movementAngle = moveAngle - Math.PI;
      }
      
      // Diagonal movement from keyboard
      if (keysPressed.current['w'] && keysPressed.current['a']) {
        movementAngle = angle - Math.PI / 4;
      }
      if (keysPressed.current['w'] && keysPressed.current['d']) {
        movementAngle = angle + Math.PI / 4;
      }
      if (keysPressed.current['s'] && keysPressed.current['a']) {
        movementAngle = angle + 3 * Math.PI / 4;
      }
      if (keysPressed.current['s'] && keysPressed.current['d']) {
        movementAngle = angle - 3 * Math.PI / 4;
      }
      
      if (moved) {
        // Use the consolidated function to check if the new position is valid
        if (isValidPosition(newPosition)) {
          // Update position only if valid
          setPosition(newPosition);
          
          // Also update the shared position state
          setPlayerPosition(newPosition);
          
          // Smoothly interpolate rotation
          if (movementAngle !== null) {
            const isFront = keysPressed.current['w'] && !keysPressed.current['s']; 
            targetRotationRef.current = movementAngle * (isFront ? -1 : 1);
            
            // Smoothly rotate to the new angle, taking the shortest path
            setRotation(current => lerpAngle(current, targetRotationRef.current, 0.1));
            lastMovementDirection.current = movementAngle;
          }
        }
      }
      
      // Apply position and rotation to the 3D object
      groupRef.current.position.set(position[0], position[1], position[2]);
      groupRef.current.rotation.y = rotation;
    }

    // Lerp between currentAngle and targetAngle
    const lerpSpeed = 0.3; // Adjust this value for faster/slower lerping
    setCurrentAngle((prev) => THREE.MathUtils.lerp(prev, targetAngle.current, lerpSpeed));
  });
  
  // Initial position update
  useEffect(() => {
    setPlayerPosition(position);
  }, []);
  
  // Add this to the Character component in Player.js
  useEffect(() => {
    // Handler for spacebar key press
    const handleKeyDown = (event) => {
      if (event.code === 'Space') {
        toggleAngle();
        
        // Dispatch a custom event to notify App.js that a dance occurred
        const danceEvent = new CustomEvent('character-danced', {
          bubbles: true
        });
        window.dispatchEvent(danceEvent);
      }
    };
    
    // Add event listener for spacebar
    window.addEventListener('keydown', handleKeyDown);
    
    // Clean up on unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);  // Empty dependency array ensures this only runs once on mount

  // Add useEffect to watch for toggleAngleFlag changes
  useEffect(() => {
    // When toggleAngleFlag changes, call toggleAngle
    toggleAngle();
  }, [toggleAngleFlag]); // Only re-run when toggleAngleFlag changes

  return (
    <>
      <group ref={groupRef}>
        {/* Main body */}
           <Trail
            width={4} // Width of the line
            color={'#44B9E3'} // Color of the line
            length={1} // Length of the line
            decay={1} // How fast the line fades away
            local={false} // Wether to use the target's world or local positions
            stride={0} // Min distance between previous and current point
            interval={1} // Number of frames to wait before next calculation
            attenuation={(width) => width} // A function to define the width in each point along it.
          >

        <group position={[0, 2.7, 0]} rotation={[0,Math.PI, 0]}>
              <mesh scale={[0.7, 1, 0.5]}>
              
                <cylinderGeometry args={[1, 0, 1, 4]} />
                <meshStandardMaterial color="#FFF" />
                
              </mesh>
              <mesh scale={[0.7, 0.3, 0.5]} rotation={[Math.PI, 0, 0]} position={[0, 0.65, 0]}>
              
                <cylinderGeometry args={[1, 0, 1, 4]} />
                <meshStandardMaterial color="#FFF" />
                
              </mesh>
                {/* Face group */}
                <group position={[0, 0.5, 0.41]}>
                  {createCircleMesh([-0.4, 0, 0])}
                  {createCircleMesh([0.4, 0, -0.1])}
                </group>
          </group>

          </Trail>
        
            <group position={[0, isFemale ? 2.2 : 0, 0]} rotation={[0, 0, currentAngle]}>
              
       
              <mesh position={[0, isFemale ? -0.9 : 1.1, 0]} scale={[isFemale ? 1.2 : 1.5, isFemale ? 1.8 : 2.2, 0.5]} rotation={[isFemale ? Math.PI : 0, 0, 0]}>
                <cylinderGeometry args={[1, 0, 1, 4]} />
                <meshStandardMaterial color="#FFF" />
              </mesh>
              {isFemale &&  <mesh position={[0, -2.05, 0]} 
              scale={[1.2, 0.5, 0.5]} 
              rotation={[0, 0, 0]}>
                <cylinderGeometry args={[1, 0, 1, 4]} />
                <meshStandardMaterial color="#FFF" />
              </mesh>}
            </group>

       
        
            <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, -0.3, 0]} scale={[1.6, 0.6, 1.6]}>
              <circleGeometry args={[1, 4]} />
              <meshBasicMaterial color="black" side={THREE.DoubleSide} transparent opacity={0.2} />
            </mesh>
          
    


      </group>

      <ThirdPersonCamera playerPosition={position} />
    </>
  );
}
